import { cloneDeep, isEqual, isNumber, omit, set } from 'lodash';
import { createCanvas as createFoundationCanvas, fixZipResUrl, imageKit, textBridge } from '@meitu/wasm-lib';
import assetsConfig from '@meitu/wasm-lib/assetsConfig.json';
import { normalizeResultScale, type IFramework, type IResultOptions } from '@/framework';
import { createFontSubsetURL } from '@/framework/services/font/subset';
import { base64Kit } from '@/utils/base64-kit';
import { createCanvas, isMd5Uri, loadImageAsync } from '@/utils/file-kit';
import { glKit } from '@/utils/gl-kit';
import { transformTextDataToLowLever } from '@/utils/transform-kit';
import {
    TextShapeType,
    type IFontBackground,
    type ILayerProps,
    type ISize,
    type ITextLayerProps,
} from '@/version-types';
import { enableAspectRatioShapeTypes, shapePlistConfigID } from '../config';
import { resolveVisibleBackground } from '../utils';

type FoundationTextLayerData = Parameters<typeof textBridge.updateTextLayerAttr>[0];
type FoundationWrapTextConfig = ReturnType<typeof textBridge.getTextWarpConfig>;
type FoundationPathTextConfig = ReturnType<typeof textBridge.getTextPathConfig>;

/**
 * Foundation Canvas 绑定的 Frame 几何快照
 */
type ShapeTextFrameInfo = {
    id: number;
    width: number;
    height: number;
    left: number;
    top: number;
};

/**
 * Shape Text Renderer 支持的渲染模式
 *
 * - `live`：更新全局 GL Canvas，供属性连续调节时实时展示
 * - `stable`：从最后成功的 GL 状态生成持久化截图，但不结束当前 live 会话
 */
export type ShapeTextRenderMode = 'live' | 'stable';

/**
 * Shape Text Canvas-only 输出结果
 */
export type ShapeTextCanvasResult = {
    /**
     * 不包含图层外层透明度的 Shape Text Canvas
     */
    canvas: HTMLCanvasElement;

    /**
     * Canvas 相对 Shape Text 图层主体的局部可见范围
     */
    renderBox: { left: number; top: number; width: number; height: number };
};

const AR_RES_ID = 'ar_res';
const AR_RES_PATH = assetsConfig.text_ar_res;
const AR_PUBLIC_PARAMS = assetsConfig.text_hbpArPublicParams;
const HBP_TEXT_RES_ID = 'hbp';
const HBP_TEXT_PATH = assetsConfig.text_effect_hbp;
const HBP_TEXT_PLIST = `${HBP_TEXT_RES_ID}/ar/configuration.plist`;
const SHAPE_TEXT_RES_ID = 'textpatheffect';
const SHAPE_TEXT_PATH = assetsConfig.text_effect_textpatheffect;

const isWrapEffect = (type: number) => type >= TextShapeType.waveShaped && type <= TextShapeType.lowerArc;
const isPathEffect = (type: number) => type <= TextShapeType.tilde;

/**
 * 管理单个编辑器内全部 Shape Text 渲染状态
 *
 * Foundation 与 GL Canvas 是编辑器进程内的共享资源，因此 Renderer 只允许一个 live owner，
 * 并通过队列和 generation 保证旧输入不能覆盖最新结果。该类不提交正式图层或历史。
 */
export class ShapeTextRenderer {
    /**
     * Shape Text 公共资源加载任务
     *
     * 同一编辑器内并发调用共享该 Promise；加载失败时清空，允许后续调用重试。
     */
    private assetReadyPromise?: Promise<void>;

    /**
     * Foundation 当前注册的字体子集 ID
     *
     * `textBridge.updateTextLayerAttr` 通过该 ID 读取 Foundation 私有文件系统中的字体。
     */
    private foundationSubFontCache = '';

    /**
     * 正处于 GL 到静态 drawable 交接期的图层 ID
     *
     * stable 图层会先清除 GL runtime 标记，但最终图片仍需异步加载。交接期继续绘制最后一帧
     * GL Canvas，直到宿主 drawable 通过 version gate 后调用 `settleDrawable` 清理对应 ID。
     */
    private readonly handoffLayerIds = new Set<string>();

    /**
     * 串行化所有 Foundation 与 GL 操作的队列尾 Promise
     *
     * 无论上一任务成功或失败，下一任务都会继续执行，避免全局 `textBridge` 状态并发互相覆盖。
     */
    private queue: Promise<void> = Promise.resolve();

    /**
     * live/stable 请求的最新代次
     *
     * live 任务在发布 GL 前校验代次，已被新请求取代的任务只能完成准备，不能发布旧画面。
     */
    private generation = 0;

    /**
     * 当前拥有全局 GL Canvas 的 Shape Text 图层 ID
     */
    private liveLayerId: string | null = null;

    /**
     * 当前 live owner 最新请求的候选图层
     *
     * stable 渲染通过该值读取尚未正式提交但已进入实时渲染的最新输入。
     */
    private desiredLayer: ILayerProps | null = null;

    /**
     * 最后一次成功发布到 GL Canvas 的图层
     *
     * stable 输入未变化时直接从该状态生成截图，保证最终图片与退出前最后一帧 GL 几何一致。
     */
    private lastLiveLayer: ILayerProps | null = null;

    /**
     * 当前 live 会话开始前的稳定图层
     *
     * live 失败、取消或切换图层时用于恢复正式视觉，不包含本轮未提交的属性变化。
     */
    private lastStableLayer: ILayerProps | null = null;

    /**
     * 当前 live 的完整初始化任务
     *
     * 首次属性请求会排在该任务之后，不能用 generation 把预热提前取消。
     */
    private glRenderReadyPromise: Promise<void> | null = null;

    /**
     * 当前 Foundation Canvas、字体与 Frame 是否已经完成 live 初始化
     */
    private glReady = false;

    /**
     * 记录 Foundation 实际完成布局的变形类型，用于判断是否需要重建底层滤镜
     *
     * 切换类型时需要重建滤镜，避免上一种 Path/Warp 配置残留；同类型调节参数时继续复用。
     * 这里不能使用 `lastLiveLayer` 的类型：它只代表已发布的画面，而被 generation 判为过期的
     * 任务仍可能已经改变底层状态。例如 A → B → A，B 已完成底层初始化但尚未发布就被取消，
     * 此时 `lastLiveLayer` 仍是 A，底层却是 B。单独记录 B，才能在后续渲染 A 时正确重建滤镜。
     * 因此该值在 `prepare` 完成布局后更新，不等待 live 发布；尚未完成初始化时为 undefined。
     */
    private foundationShapeType: TextShapeType | undefined;

    /**
     * 当前 Foundation Canvas 对应的 Frame 几何快照
     *
     * Frame ID、尺寸或长图显示原点变化时必须重新执行 `prepare`，不能复用旧 Canvas。
     */
    private frameInfo: ShapeTextFrameInfo | null = null;

    /**
     * 当前 live 会话进入 Foundation 前的原始图层几何
     *
     * `resolveLayerLayout` 始终基于该输入几何计算变形结果，不能用已经变形后的输出几何覆盖。
     */
    private layout = { width: 0, height: 0, left: 0, top: 0, rotate: 0 };

    constructor(private readonly framework: IFramework) {}

    /**
     * 预加载 Shape Text 使用的 Foundation 资源
     *
     * @returns 资源全部就绪后的 Promise
     */
    preload(): Promise<void> {
        if (this.assetReadyPromise) return this.assetReadyPromise;

        const promise = Promise.all([
            textBridge.loadTextPathEffectZip(HBP_TEXT_RES_ID, fixZipResUrl(HBP_TEXT_PATH)),
            textBridge.loadTextPathEffectZip(AR_RES_ID, fixZipResUrl(AR_RES_PATH)),
            textBridge.loadTextPathEffectZip(SHAPE_TEXT_RES_ID, fixZipResUrl(SHAPE_TEXT_PATH)),
            import('../assets/AlibabaPuHuiTi-Regular.zip').then(({ default: url }) =>
                textBridge.registerFont('AlibabaPuHuiTi-Regular', url),
            ),
            textBridge.registerArPublic(fixZipResUrl(AR_PUBLIC_PARAMS)),
        ])
            .then(() => undefined)
            .catch((error) => {
                if (this.assetReadyPromise === promise) this.assetReadyPromise = undefined;
                throw error;
            });

        this.assetReadyPromise = promise;
        return promise;
    }

    /**
     * 让 GL 接管目标图层，并为已有 Shape Text 完成一次不可被属性请求取消的 live 初始化
     *
     * 普通文字只启动 live 生命周期，等待应用 Shape Text 后由正常 live 请求完成准备。同一图层重复调用
     * 会复用正在执行的初始化任务；切换图层时立即放弃旧 live 状态。
     *
     * @param layer - 当前宿主对应的正式 Text Layer
     * @returns 预热完成后的 Promise
     */
    startGLRender(layer: ILayerProps): Promise<void> {
        const layerId = layer.id;
        if (this.liveLayerId === layerId) {
            if (this.glRenderReadyPromise) return this.glRenderReadyPromise;
            if (this.glReady) return Promise.resolve();
            if (!this.lastStableLayer) {
                this.lastStableLayer = cloneDeep(this.framework.service.flatLayer.getLayer(layerId, true) || layer);
            }
        } else {
            this.glRenderReadyPromise = null;
            if (this.liveLayerId && this.liveLayerId !== layerId) {
                this.generation += 1;
                this.clearLiveState();
            }
            this.liveLayerId = layerId;
            this.lastStableLayer = cloneDeep(this.framework.service.flatLayer.getLayer(layerId, true) || layer);
            this.glReady = false;
            this.frameInfo = null;
        }

        if (!(layer.data as ITextLayerProps).shapeExtension) return Promise.resolve();

        const requestedLayer = cloneDeep(layer);
        this.desiredLayer = cloneDeep(layer);
        this.generation += 1;

        const ready = this.enqueue(async () => {
            if (this.liveLayerId !== layerId) return;
            const source = this.desiredLayer?.id === layerId ? cloneDeep(this.desiredLayer) : requestedLayer;
            const rendered = await this.resolveLiveLayer(source);
            if (this.liveLayerId !== layerId) return;
            this.publishLiveLayer(rendered);
        });
        this.glRenderReadyPromise = ready;
        const clearReady = () => {
            if (this.glRenderReadyPromise === ready) this.glRenderReadyPromise = null;
        };
        void ready.then(clearReady, clearReady);
        return ready;
    }

    /**
     * 解析 Shape Text 的实时或稳定结果
     *
     * @param layer - 当前 Shape Text 候选图层
     * @param mode - live 实时渲染或 stable 稳定图片结果
     * @returns 当前仍可发布的候选；被更新输入取代时返回 null
     */
    render(layer: ILayerProps, mode: ShapeTextRenderMode): Promise<ILayerProps | null> {
        const requestedLayer = cloneDeep(layer);
        const requestGeneration = mode === 'stable' ? this.generation : ++this.generation;

        if (mode === 'live') {
            if (this.liveLayerId !== layer.id) {
                this.liveLayerId = layer.id;
                this.lastStableLayer = cloneDeep(this.framework.service.flatLayer.getLayer(layer.id, true) || layer);
                this.glReady = false;
                this.frameInfo = null;
            } else if (!this.lastStableLayer) {
                this.lastStableLayer = cloneDeep(this.framework.service.flatLayer.getLayer(layer.id, true) || layer);
            }
            this.desiredLayer = cloneDeep(layer);
        }

        return this.enqueue(async () => {
            if (mode === 'live' && requestGeneration !== this.generation) return null;

            const source =
                mode === 'stable' && this.liveLayerId === layer.id && this.desiredLayer
                    ? cloneDeep(this.desiredLayer)
                    : requestedLayer;

            if (mode === 'live') {
                const rendered = await this.resolveLiveLayer(source);

                if (requestGeneration !== this.generation) return null;
                this.publishLiveLayer(rendered);
                return rendered;
            }

            const canReuseLiveFoundation =
                this.liveLayerId === source.id &&
                this.glReady &&
                this.foundationShapeType === (source.data as ITextLayerProps).shapeExtension?.curveTextType &&
                !this.hasFrameChanged(source.id) &&
                this.lastLiveLayer !== null &&
                isEqual(source, this.lastLiveLayer);

            let rendered: ILayerProps;
            if (canReuseLiveFoundation) {
                rendered = cloneDeep(this.lastLiveLayer as ILayerProps);
            } else if (this.liveLayerId === source.id) {
                rendered = await this.resolveLiveLayer(source);
                this.publishLiveLayer(rendered);
            } else {
                rendered = await this.prepare(source);
            }

            const screenshotUrl = await this.renderScreenshot(rendered);
            if (!screenshotUrl) throw new Error('Shape text result image is empty.');

            const result = set(cloneDeep(rendered), 'data.screenshotUrl', screenshotUrl);
            glKit.clearGL(result);
            return result;
        });
    }

    /**
     * 为 Edgeless 宿主创建 Shape Text drawable
     *
     * @param layer - 当前正式图层
     * @returns 背景占位或背景与稳定截图的完整 Canvas
     */
    async loadDrawable(layer: ILayerProps): Promise<HTMLCanvasElement> {
        if (glKit.isUseGL(layer)) {
            this.handoffLayerIds.add(layer.id);
            return this.createLayerCanvas(layer, true);
        }
        return this.createLayerCanvas(layer, false);
    }

    /**
     * 在 Edgeless 宿主中绘制 Shape Text 实时 GL 结果
     *
     * @param layer - 当前 Shape Text 图层
     * @param ctx - 宿主 Canvas 上下文
     * @param hostSize - 当前宿主局部尺寸
     * @returns 本轮是否由 Shape Text Renderer 接管绘制
     */
    draw(layer: ILayerProps, ctx: CanvasRenderingContext2D, hostSize: ISize): boolean {
        if (!(layer.data as ITextLayerProps).shapeExtension) return false;
        const isLive = this.liveLayerId === layer.id && this.lastLiveLayer !== null;
        if (!glKit.isUseGL(layer) && !this.handoffLayerIds.has(layer.id) && !isLive) return false;

        ctx.drawImage(this.createLayerCanvas(layer, true), 0, 0, hostSize.width, hostSize.height);
        this.drawGLCanvas(layer, ctx);

        return true;
    }

    /**
     * 生成 Shape Text 的 Canvas-only 输出结果
     *
     * @param layer - 当前 Shape Text 图层
     * @param options - 导出缩放配置
     * @returns Canvas 与图层局部可见范围
     */
    async getResult(layer: ILayerProps, options: IResultOptions = {}): Promise<ShapeTextCanvasResult> {
        const stableLayer =
            glKit.isUseGL(layer) || this.liveLayerId === layer.id
                ? await this.render(layer, 'stable')
                : cloneDeep(layer);
        if (!stableLayer) throw new Error('Shape text stable render was superseded.');

        const sourceCanvas = await this.createLayerCanvas(stableLayer, false);
        const scale = normalizeResultScale(options.scale);
        const width = Math.max(1, Math.round(stableLayer.width * scale));
        const height = Math.max(1, Math.round(stableLayer.height * scale));

        let canvas = sourceCanvas;

        if (sourceCanvas.width !== width || sourceCanvas.height !== height) {
            const target = document.createElement('canvas');
            target.width = width;
            target.height = height;

            const ctx = target.getContext('2d');
            if (!ctx) throw new Error('Canvas 2D context is unavailable for shape text export.');

            ctx.drawImage(sourceCanvas, 0, 0, width, height);
            canvas = target;
        }

        return {
            canvas,
            renderBox: { left: 0, top: 0, width: stableLayer.width, height: stableLayer.height },
        };
    }

    /**
     * 固化目标图层的本次 live 结果并开始 drawable 交接
     *
     * stable 图片必须先生成完成，再调用该方法。Renderer 会继续保留 live 状态，直到调用方显式执行
     * `finishGLRender`；最后一帧 GL Canvas 则保留到宿主 drawable 通过 version gate。
     *
     * @param layer - 本次 live 渲染已接受的 stable Shape Text 图层
     * @returns 无返回值
     */
    settleLive(layer: ILayerProps): void {
        if (this.liveLayerId !== layer.id) return;
        this.lastStableLayer = cloneDeep(layer);
        if (this.lastLiveLayer) this.handoffLayerIds.add(layer.id);
    }

    /**
     * 结束指定宿主的 GL 渲染，并启动最后 GL 帧到正式 drawable 的交接
     *
     * 非当前 live owner 的旧宿主不能释放新图层的全局 GL 状态。
     *
     * @param layerId - 结束 GL 渲染的宿主图层 ID
     * @returns 等待在途渲染后是否结束了当前 live
     */
    async finishGLRender(layerId: string): Promise<boolean> {
        if (this.liveLayerId !== layerId) return false;

        while (this.liveLayerId === layerId) {
            const pending = this.queue;
            await pending;
            await Promise.resolve();
            if (pending === this.queue) break;
        }

        if (this.liveLayerId !== layerId) return false;

        this.glRenderReadyPromise = null;

        if (this.lastLiveLayer) this.handoffLayerIds.add(layerId);

        this.generation += 1;
        this.clearLiveState();

        return true;
    }

    /**
     * 判断目标图层是否拥有当前 live
     *
     * @param layerId - 需要检查的 Shape Text 图层 ID
     * @returns 目标图层正在 live 时返回 true
     */
    isLive(layerId: string): boolean {
        return this.liveLayerId === layerId;
    }

    /**
     * 通知 Renderer 最新正式 drawable 已通过宿主版本校验
     *
     * @param layerId - 已完成资源发布的图层 ID
     * @param _ready - 本轮 drawable 是否成功发布
     */
    settleDrawable(layerId: string, _ready: boolean): void {
        this.handoffLayerIds.delete(layerId);
    }

    /**
     * 放弃当前 live 状态并返回最后稳定图层
     *
     * @param layerId - 可选的目标图层 ID；不匹配当前 owner 时不处理
     * @param options - 是否保留 live owner，仅清理 Foundation/GL 派生状态
     * @returns 最后稳定图层；当前没有匹配 live 时返回 null
     */
    reset(layerId?: string, options: { keepLive?: boolean } = {}): ILayerProps | null {
        if (!this.liveLayerId || (layerId && this.liveLayerId !== layerId)) return null;

        this.handoffLayerIds.delete(this.liveLayerId);
        const stable = this.lastStableLayer ? cloneDeep(this.lastStableLayer) : null;
        if (stable) glKit.clearGL(stable);

        this.generation += 1;
        this.clearLiveState(options.keepLive);

        return stable;
    }

    /**
     * 把 Foundation/GL 异步任务追加到全局串行队列
     *
     * 队列尾始终转换为 fulfilled，确保某一轮渲染失败不会阻塞后续任务；调用方仍收到原始任务结果。
     *
     * @param task - 需要独占 Foundation/GL 状态执行的异步任务
     * @returns 当前任务的原始结果 Promise
     */
    private enqueue<T>(task: () => Promise<T>): Promise<T> {
        const result = this.queue.then(task, task);
        this.queue = result.then(
            () => undefined,
            () => undefined,
        );
        return result;
    }

    /**
     * 清空当前 live owner 及其派生状态
     *
     * 该方法不清理 handoff ID；stable drawable 完成交接前仍需使用最后的 GL Canvas。
     *
     * @param keepOwner - 是否只清理 GL 派生状态并保留当前 live owner
     * @returns 无返回值
     */
    private clearLiveState(keepOwner = false) {
        if (!keepOwner) this.liveLayerId = null;
        this.desiredLayer = null;
        this.lastLiveLayer = null;
        this.lastStableLayer = null;
        this.glReady = false;
        this.frameInfo = null;
    }

    /**
     * 解析下一份可发布到 GL Canvas 的 Shape Text 图层
     *
     * Foundation 尚未初始化、Frame 或变形类型已变化时执行完整 `prepare`；否则只更新文字属性、
     * Shape 配置与变形布局。该方法不调用 `glKit.processGL`，因此不会自行发布可见画面。
     *
     * @param source - 即将进入 live 渲染的候选图层
     * @returns 已完成 Foundation 布局解析的图层
     */
    private async resolveLiveLayer(source: ILayerProps): Promise<ILayerProps> {
        const shapeType = (source.data as ITextLayerProps).shapeExtension?.curveTextType;
        if (!this.glReady || this.hasFrameChanged(source.id) || this.foundationShapeType !== shapeType) {
            this.glReady = false;
            const rendered = await this.prepare(source);
            this.glReady = true;
            this.frameInfo = this.getFrameInfo(source.id);
            return rendered;
        }
        return { ...source, ...this.resolveLayerLayout(source) };
    }

    /**
     * 把已解析图层发布为当前全局 GL 帧
     *
     * 同时保存最后成功的 live 图层，供 stable 截图判断是否可以复用同一 Foundation 状态。
     *
     * @param rendered - 已完成 Foundation 布局解析的图层
     * @returns 无返回值
     */
    private publishLiveLayer(rendered: ILayerProps) {
        glKit.openGL(rendered);
        glKit.processGL(rendered);
        this.desiredLayer = cloneDeep(rendered);
        this.lastLiveLayer = cloneDeep(rendered);
    }

    /**
     * 读取图层所属 Frame 的 Foundation 坐标信息
     *
     * 长图模式下移除 `offsetTop`，得到 Foundation 舞台使用的本地 Frame 原点；图层不属于
     * 任何 Frame 时抛出错误，避免把全局 GL Canvas 绑定到错误舞台。
     *
     * @param layerId - Shape Text 图层 ID
     * @returns Frame ID、尺寸与 Foundation 本地原点
     */
    private getFrameInfo(layerId: string): ShapeTextFrameInfo {
        const frame = this.framework.service.frame;
        const frameId = frame.getLayerFrameId(layerId);
        if (!isNumber(frameId)) throw new Error(`Shape text layer has no owning frame: ${layerId}`);

        const size = frame.getSize(frameId);
        const baseInfo = frame.getBaseInfo(frameId);
        const { offsetTop } = frame.getFrameTransform(frameId);

        return {
            id: frameId,
            width: size.width,
            height: size.height,
            left: baseInfo.left,
            top: baseInfo.top - offsetTop,
        };
    }

    /**
     * 检查当前 Foundation Canvas 是否仍匹配图层所属 Frame
     *
     * @param layerId - Shape Text 图层 ID
     * @returns Frame ID、尺寸或本地原点任一变化时返回 true
     */
    private hasFrameChanged(layerId: string) {
        if (!this.frameInfo) return true;
        const current = this.getFrameInfo(layerId);
        return (
            current.id !== this.frameInfo.id ||
            current.width !== this.frameInfo.width ||
            current.height !== this.frameInfo.height ||
            current.left !== this.frameInfo.left ||
            current.top !== this.frameInfo.top
        );
    }

    /**
     * 为候选图层完整初始化 Foundation Shape Text 环境
     *
     * 依次确保公共资源、创建 Frame 尺寸 Canvas、注册当前文本字体子集，并基于输入图层
     * 几何解析第一份变形布局。该方法只准备 Foundation，不发布正式图层或历史。
     *
     * @param layer - 需要完整初始化的 Shape Text 候选图层
     * @returns 已应用 Foundation 变形布局的图层
     */
    private async prepare(layer: ILayerProps): Promise<ILayerProps> {
        await this.preload();

        const { fontFamily, text } = layer.data as ITextLayerProps;
        const fontSource = this.framework.service.font.getByFamily(fontFamily)?.ttf;
        if (!fontSource) throw new Error(`Shape text font source is unavailable: ${fontFamily}`);

        this.layout = {
            width: layer.width,
            height: layer.height,
            left: layer.left,
            top: layer.top,
            rotate: layer.rotate,
        };

        const frameInfo = this.getFrameInfo(layer.id);
        await textBridge
            .from(createFoundationCanvas(frameInfo.width, frameInfo.height))
            .apply(
                HBP_TEXT_RES_ID,
                fixZipResUrl(HBP_TEXT_PATH),
                HBP_TEXT_PLIST,
                AR_RES_ID,
                fixZipResUrl(AR_RES_PATH),
                undefined,
                true,
            );

        this.foundationSubFontCache = await textBridge.registerSubFont(
            fontFamily,
            text,
            createFontSubsetURL(fontSource, text, 'ttf'),
        );
        if (!this.foundationSubFontCache) throw new Error(`Failed to register shape text font subset: ${fontFamily}`);

        const rendered = { ...layer, ...this.resolveLayerLayout(layer) };
        this.foundationShapeType = (layer.data as ITextLayerProps).shapeExtension?.curveTextType;
        return rendered;
    }

    /**
     * 把当前 Shape Text 数据写入 Foundation 并计算变形后几何
     *
     * 方法使用 `layout` 保存的原始输入几何调用 `refreshLayerPosition`，返回值转换回编辑器 Frame
     * 坐标。输入没有 `shapeExtension` 时返回空对象，调用方可安全展开到原图层。
     *
     * @param layer - 当前 Shape Text 候选图层
     * @returns 变形后的位置与尺寸；没有 Shape Text 配置时返回空对象
     */
    private resolveLayerLayout(layer: ILayerProps) {
        const shapeExtension = (layer.data as ITextLayerProps).shapeExtension;
        if (!shapeExtension) return {};

        const frameInfo = this.getFrameInfo(layer.id);
        textBridge.updateTextLayerAttr(
            transformTextDataToLowLever(cloneDeep(layer.data as ITextLayerProps)) as unknown as FoundationTextLayerData,
            layer.width,
            layer.height,
            frameInfo.width,
            false,
            this.foundationSubFontCache,
        );

        const shapeType = shapeExtension.curveTextType;
        const wrap = isWrapEffect(shapeType);
        const plistPath = `${SHAPE_TEXT_RES_ID}/${shapePlistConfigID[shapeType]}/ar/configuration.plist`;
        textBridge[wrap ? 'setTextWarpPlist' : 'setTextPathPlist'](plistPath);
        const config = textBridge[wrap ? 'getTextWarpConfig' : 'getTextPathConfig']();

        if (isPathEffect(shapeType)) {
            (config as FoundationPathTextConfig).enable = true;
            (config as FoundationPathTextConfig).enableAspectRatio = enableAspectRatioShapeTypes[shapeType];
        }
        Object.entries(shapeExtension).forEach(([key, value]) => {
            if (value !== undefined) Reflect.set(config, key, value);
        });
        if (wrap) textBridge.setTextWarpConfig(config as FoundationWrapTextConfig);
        else textBridge.setTextPathConfig(config as FoundationPathTextConfig);

        const next = textBridge.refreshLayerPosition(
            this.layout.left - frameInfo.left,
            this.layout.top - frameInfo.top,
            this.layout.width,
            this.layout.height,
            this.layout.rotate,
        );
        return { ...next, left: next.left + frameInfo.left, top: next.top + frameInfo.top };
    }

    /**
     * 从当前 Foundation 状态生成稳定截图 URL
     *
     * 根据不含旧截图和富文本派生分段的输入生成内容指纹；已有本地缓存时直接返回指纹，
     * 否则读取 Foundation Canvas、编码图片并写入 Base64 缓存。
     *
     * @param layer - 与当前 Foundation 状态对应的 Shape Text 图层
     * @returns 稳定截图 URL；Foundation 未产出 Canvas 时返回 null
     */
    private async renderScreenshot(layer: ILayerProps): Promise<string | null> {
        const data = omit(cloneDeep(layer.data as ITextLayerProps), ['screenshotUrl', 'textContents']);
        const params = JSON.stringify({ width: layer.width, height: layer.height, ...data });
        const md5 = base64Kit.md5(params);
        if (!isMd5Uri(base64Kit.getLocalUrl(md5))) return md5;

        const stageWidth = this.getFrameInfo(layer.id).width;
        const result = await textBridge.getTransformResultWithScale(stageWidth < 400 ? 400 / stageWidth : 1);
        if (!result.canvas) return null;

        return base64Kit.getCode(result.canvas.toDataURL(), params);
    }

    /**
     * 创建 Shape Text 的图层局部 Canvas
     *
     * live/handoff 阶段只同步绘制背景，正文由 Frame 级 GL Canvas 负责；稳定阶段异步加载
     * `screenshotUrl`，按图层 flip 规则绘制背景与正文。Canvas 始终使用向上取整后的像素尺寸。
     *
     * @param layer - 当前 Shape Text 图层
     * @param usingGL - 是否只创建供 GL/handoff 使用的背景占位 Canvas
     * @returns GL 模式同步返回 Canvas；稳定模式返回加载截图后的 Canvas Promise
     */
    private createLayerCanvas(layer: ILayerProps, usingGL: true): HTMLCanvasElement;

    /**
     * 创建包含稳定截图的 Shape Text 图层局部 Canvas
     *
     * @param layer - 当前 Shape Text 图层
     * @param usingGL - 固定为 false，表示等待并绘制稳定截图
     * @returns 加载截图并完成绘制后的 Canvas Promise
     */
    private createLayerCanvas(layer: ILayerProps, usingGL: false): Promise<HTMLCanvasElement>;

    /**
     * 实现 Shape Text 背景占位与稳定截图的 Canvas 创建
     *
     * @param layer - 当前 Shape Text 图层
     * @param usingGL - 是否跳过稳定截图加载
     * @returns 同步背景 Canvas 或异步稳定 Canvas
     */
    private createLayerCanvas(layer: ILayerProps, usingGL: boolean): HTMLCanvasElement | Promise<HTMLCanvasElement> {
        const width = Math.ceil(layer.width);
        const height = Math.ceil(layer.height);
        const { canvas, ctx } = createCanvas(width, height);
        const background = resolveVisibleBackground((layer.data as ITextLayerProps).textBackground);
        if (background) this.drawBackground(ctx, background, width, height);
        if (usingGL) return canvas;

        return (async () => {
            // 本地截图已在生成时缓存，舞台显示无需等待后台上传完成
            const image = await loadImageAsync((layer.data as ITextLayerProps).screenshotUrl as string);
            const scaleX = layer.flipH ? -1 : 1;
            const scaleY = layer.flipV ? -1 : 1;
            ctx.save();
            ctx.scale(scaleX, scaleY);
            ctx.drawImage(image, scaleX < 0 ? -width : 0, scaleY < 0 ? -height : 0, width, height);
            ctx.restore();
            return canvas;
        })();
    }

    /**
     * 在图层局部 Canvas 中绘制 Shape Text 可见背景
     *
     * 圆角半径会被限制在短边的一半以内，避免异常素材数据产生自交路径。
     *
     * @param ctx - 目标图层局部 Canvas 上下文
     * @param background - 已解析的可见文字背景
     * @param width - Canvas 像素宽度
     * @param height - Canvas 像素高度
     * @returns 无返回值
     */
    private drawBackground(ctx: CanvasRenderingContext2D, background: IFontBackground, width: number, height: number) {
        const radius = Math.min(Math.max(0, background.roundWeight ?? 0), width / 2, height / 2);
        ctx.fillStyle = background.color;
        ctx.beginPath();
        ctx.moveTo(radius, 0);
        ctx.arcTo(width, 0, width, height, radius);
        ctx.arcTo(width, height, 0, height, radius);
        ctx.arcTo(0, height, 0, 0, radius);
        ctx.arcTo(0, 0, width, 0, radius);
        ctx.closePath();
        ctx.fill();
    }

    /**
     * 把 Frame 坐标系中的全局 GL Canvas 合成到当前 Edgeless 宿主
     *
     * 主视口使用 zoom/translate 变换，Frame 离屏 Canvas 使用 Retina 缩放；Foundation 已经把
     * 图层位置和旋转烘焙进 GL Canvas，因此这里覆盖调用方局部变换，只额外补偿图层 flip。
     *
     * @param layer - 当前 Shape Text 图层
     * @param ctx - 主视口或 Frame 离屏 Canvas 上下文
     * @returns 无返回值；GL Canvas 或所属 Frame 不可用时直接结束
     */
    private drawGLCanvas(layer: ILayerProps, ctx: CanvasRenderingContext2D) {
        const glCanvas = imageKit.getGLCanvas() as HTMLCanvasElement | null;
        if (!glCanvas) return;

        const { edgelessCanvas, frame } = this.framework.service;
        const frameId = frame.getLayerFrameId(layer.id);
        if (frameId == null) return;
        const isFrameCanvas = ctx.canvas === frame.get(frameId)?.canvas;
        const frameBox = frame.getBaseInfo(frameId);
        const { offsetTop } = frame.getFrameTransform(frameId);
        const localFrameBox = { ...frameBox, top: frameBox.top - offsetTop };
        const destination = isFrameCanvas
            ? { left: 0, top: 0, width: frameBox.width, height: frameBox.height }
            : { left: frameBox.left, top: frameBox.top, width: frameBox.width, height: frameBox.height };
        const center = isFrameCanvas
            ? {
                  x: layer.left - localFrameBox.left + layer.width / 2,
                  y: layer.top - localFrameBox.top + layer.height / 2,
              }
            : { x: layer.left + layer.width / 2, y: layer.top + offsetTop + layer.height / 2 };

        ctx.save();
        if (isFrameCanvas) {
            const retinaScaling = edgelessCanvas.retinaScaling;
            ctx.setTransform(retinaScaling, 0, 0, retinaScaling, 0, 0);
        } else {
            const { zoom, translateX, translateY } = edgelessCanvas.renderZoomAndTranslate;
            ctx.setTransform(zoom, 0, 0, zoom, translateX, translateY);
        }

        if (layer.flipH || layer.flipV) {
            const radians = (layer.rotate * Math.PI) / 180;
            ctx.translate(center.x, center.y);
            ctx.rotate(radians);
            ctx.scale(layer.flipH ? -1 : 1, layer.flipV ? -1 : 1);
            ctx.rotate(-radians);
            ctx.translate(-center.x, -center.y);
        }
        ctx.drawImage(glCanvas, destination.left, destination.top, destination.width, destination.height);
        ctx.restore();
    }
}
